import { ProviderDiagnostic, SearchDiagnostics } from '@appTypes/api.types';

export interface ProgressSnapshot {
  complete: boolean;
  pending?: readonly string[] | null;
  unavailable?: readonly string[] | null;
  /** supplier codes seen on the listed offers (a combination "A + B" counts for both) */
  codes?: readonly (string | null | undefined)[];
}

/**
 * Supplier timings measured in the browser from the progressive answers (run 24) - for a search whose answer does not carry `diagnostics`
 * yet (hotels). A supplier answered when it left `providersPending` (time from the search request to that answer, so within one poll interval);
 * one never seen pending answered before the first answer. Named in `providersUnavailable`: still pending when the search completed =
 * no answer (given up), otherwise an error. Offers per supplier are not known here (`results` stays empty).
 */
export class SupplierTimingTracker {
  #t0 = 0;
  #firstAt: number | undefined;
  #lastPending = new Set<string>();
  #namedPending = false;
  readonly #suppliers = new Map<string, { pendingSeen: boolean; answeredAt?: number; gaveUp?: boolean }>();

  start(now: number) {
    this.#t0 = now;
    this.#firstAt = undefined;
    this.#lastPending = new Set();
    this.#namedPending = false;
    this.#suppliers.clear();
  }

  observe(s: ProgressSnapshot, now: number): SearchDiagnostics | null {
    this.#firstAt ??= now;
    const pending = new Set((s.pending ?? []).filter(Boolean));
    const unavailable = new Set((s.unavailable ?? []).filter(Boolean));
    const names = new Set<string>([...this.#suppliers.keys(), ...pending, ...unavailable]);
    // the supplier codes on the offers only while no answer named the suppliers still asked (those are the channels' own suppliers)
    if (pending.size) this.#namedPending = true;
    if (!this.#namedPending)
      for (const code of s.codes ?? [])
        for (const part of (code ?? '').split('+')) if (part.trim()) names.add(part.trim());

    for (const name of names) {
      const e = this.#suppliers.get(name) ?? { pendingSeen: false };
      if (pending.has(name)) e.pendingSeen = true;
      else if (e.answeredAt === undefined) {
        e.answeredAt = e.pendingSeen ? now : this.#firstAt;
        e.gaveUp = s.complete && this.#lastPending.has(name) && unavailable.has(name);
      }
      this.#suppliers.set(name, e);
    }
    this.#lastPending = pending;
    if (!this.#suppliers.size) return null;

    const providers: ProviderDiagnostic[] = [...this.#suppliers.entries()].map(([provider, e]) => {
      const ms = Math.max(0, Math.round((e.answeredAt ?? now) - this.#t0));
      if (unavailable.has(provider)) return { provider, ms, outcome: e.gaveUp ? 'timeout' : 'failed', results: null };
      if (e.answeredAt === undefined)
        return { provider, ms, outcome: s.complete ? 'timeout' : 'partial', results: null };
      return { provider, ms, outcome: 'ok', results: null };
    });
    return { providers, ...(s.complete ? { totalMs: Math.max(0, Math.round(now - this.#t0)) } : {}) };
  }
}
